import { countedKey } from "@/lib/i18n/plural";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { endpoints } from "@/lib/api/endpoints";
import { useTranslate } from "@/lib/i18n";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { useErrorMessage } from "@/shared/errors";
import {
  Button,
  EmptyState,
  ErrorState,
  LoadingSkeleton,
  ToggleChipButton,
} from "@a2z/ui";
import { Screen, TillPhoneControls, useMoney } from "@/shared/ui";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { DrawerStrip } from "@/modules/pos/components/DrawerStrip";
import { OlderChequesButton } from "@/modules/pos/components/OlderCheques";
import { hasPermission, PERMISSIONS, useSession } from "@/modules/auth/useSession";
import type { TableGridArea } from "@/lib/api/types";
import { channelLabel } from "@/lib/invoice/place";
import type { InvoiceHandoff } from "@/modules/pos/pages/InvoicePage";
import { PaperTillBarLeading } from "@/modules/pos/components/PaperRegister";
import { usePaperTill } from "@/shared/posTheme";
import { CashierWaitingLane } from "@/modules/pos/components/CashierWaitingLane";
import {
  FLOOR_TAB_ICONS,
  FloorTileKitchenLine,
  FLOOR_NEED_GROUPS,
  PaperAreaSection,
  PaperFloorLegend,
  PaperFloorNeeds,
  PaperFloorTabs,
  PaperFloorSkeleton,
  PaperFloorTile,
  PaperFloorToolbar,
  useMinuteClock,
  type FloorNeedGroup,
  closedSinceLabel,
} from "@/modules/pos/components/PaperFloor";
import {
  useOffPremiseBills,
  useOffPremiseStart,
} from "@/modules/pos/components/OffPremiseFloor";
import { RiderSittings } from "@/modules/pos/components/RiderSittings";
import { useFloorDispatch } from "@/modules/pos/components/dispatch/useFloorDispatch";
import {
  OnlineOrdersBadge,
  OnlineOrdersStrip,
} from "@/modules/pos/components/OnlineOrdersStrip";
import {
  PaperKitchenView,
  useFloorKitchenTab,
} from "@/modules/kitchen/components/PaperKitchenView";
import {
  busyCount,
  floorCard,
  floorTileFacts,
  type FloorCard,
} from "@/modules/pos/lib/floorTile";
import { serviceRequestsByTable } from "@/modules/pos/serviceRequests/serviceRequests";
import { useServiceRequests } from "@/modules/pos/serviceRequests/useServiceRequests";
import { TILL_RETURN_PARAM, tillReturnPath } from "@/modules/pos/lib/tillReturn";
import { canUseBackOffice, holdsAdministrationPermission } from "@/modules/auth/backOffice";
import {
  FloorDeliveryTab,
  deliveryTabCommands,
  useFloorDeliveryTab,
} from "@/modules/pos/components/delivery/FloorDeliveryTab";
import { useDeliveredToasts } from "@/modules/pos/lib/useDeliveredToasts";
import { offPremiseTabAvailable } from "@/modules/pos/lib/deliveryWaiting";

/**
 * The floor.
 *
 * Refetches on an interval **and on window focus** — the focus case is the one
 * that matters in practice: a waiter's phone sleeps in their pocket between
 * tables, and the first thing they do on waking it is look at this screen.
 *
 * Visual language is the POS redesign (`Trioty POS.dc.html`, floor artboard):
 * 228px-minimum tiles on a 12px gutter, a 15px radius, an 18px/800 table name
 * over a tabular running total, and an uppercase status pill. The palette comes
 * from the platform tokens `.pos-dark` remaps — no design-local token names.
 */

/** "من ٤٥ د" / "45m" — how long the table has been open. Coarse on purpose:
 *  a floor glance needs "new vs stuck", not a stopwatch. */
function formatOpenFor(
  openedAt: string,
  t: ReturnType<typeof useTranslate>,
): string {
  const minutes = Math.max(
    0,
    Math.floor((Date.now() - new Date(openedAt).getTime()) / 60000),
  );
  if (minutes >= 60) {
    return t((m) => m.pos.openForHours, { count: Math.floor(minutes / 60) });
  }
  return t((m) => m.pos.openForMinutes, { count: minutes });
}

/** The PAPER floor's views (`?view=`): Tables, Kitchen, Delivery & takeaway. */
type FloorView = "tables" | "kitchen" | "delivery";

export function TableGridPage() {
  const t = useTranslate();
  const errorMessage = useErrorMessage();
  const money = useMoney();
  const navigate = useNavigate();
  const { locale = "ar" } = useParams();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  /* Waiter phone (2026-10-28): below `lg` the floor draws the design's one
     head line, one filter row and one grid of every table (busy first). */
  const phone = useIsMobile();
  // Area filter, mockup-style: chips are the right control here (a filter over
  // one grid, not navigation), per the tabs-vs-chips rule in the interaction
  // contract. null = every area, shown as stacked sections.
  const [areaFilter, setAreaFilter] = useState<string | null>(null);
  /*
   * "WHICH TABLE IS WAITING TO PAY ME?" — the cashier's own question, and
   * until now the one state on this screen with no way to reach it.
   *
   * The tile has carried `data-state="pay"` since the cheque-out state was
   * added, and the header legend PROMISES the state in words («الحساب برّه»)
   * — so the data was there and the affordance was not: on a floor of eight
   * occupied tables, finding the one with a cheque out meant reading every
   * tile. This is a filter over the same grid, exactly like the area chips
   * beside it, and it composes with them rather than replacing them.
   */
  const [chequeOutOnly, setChequeOutOnly] = useState(false);
  // The PAPER floor (2026-10 redesign) on a light till; every other look and
  // the dark till keep the tiles below.
  const paper = usePaperTill();
  const now = useMinuteClock();

  // Checked BEFORE the fetch, like every sibling screen: `GET
  // /restaurant/invoices/grid` is `RESTAURANT_POS_USE`, so a cook or a manager
  // who types this URL would otherwise get a 403 rendered as a failure. The
  // deny state below is the kitchen board's, which is the house pattern.
  const canSee = hasPermission(session, PERMISSIONS.POS_USE);

  const { data, isLoading, error } = useApiQuery<TableGridArea[]>(
    queryKeys.grid,
    endpoints.restaurant.grid,
    {
      enabled: canSee,
      refetchOnWindowFocus: true,
    },
  );
  // D132 — the tables' open diner asks, a badge each on the PAPER tile. The
  // same revision-driven query the chime reads, so this adds no request.
  const asksByTable = serviceRequestsByTable(useServiceRequests().data);
  // 2026-10 floor — "Needs you now": a state's chip narrows the floor to it.
  const [needFilter, setNeedFilter] = useState<FloorNeedGroup | null>(null);
  // Each table's card (state, time in it, late), read once for the bar, the
  // area headings and the filter. The tiles derive their own from the same facts.
  const cards = new Map<string, FloorCard>();
  for (const area of data ?? []) {
    for (const table of area.tables) {
      cards.set(
        table.tableId,
        floorCard(table, floorTileFacts(table), asksByTable.get(table.tableId), now),
      );
    }
  }
  const needAreas = (data ?? []).filter(
    (area) => areaFilter === null || area.areaId === areaFilter,
  );
  const needCounts = Object.fromEntries(
    FLOOR_NEED_GROUPS.map((group) => [
      group,
      needAreas.reduce(
        (sum, area) =>
          sum + area.tables.filter((table) => cards.get(table.tableId)?.group === group).length,
        0,
      ),
    ]),
  ) as Record<FloorNeedGroup, number>;
  const lateIn = (tables: readonly TableGridArea["tables"][number][]) =>
    tables.filter((table) => cards.get(table.tableId)?.late).length;

  useEffect(() => {
    const onFocus = () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.grid });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [queryClient]);

  const canOpen = hasPermission(session, PERMISSIONS.INVOICE_OPEN);
  // The till's money line: takeaway and delivery are run from here, not by a waiter.
  const canClose = hasPermission(session, PERMISSIONS.INVOICE_CLOSE);
  // D154 — the staff-meal door ends in a STAFF_MEAL comp, so it is offered on
  // the comp rung (cashier, manager, owner), never to a waiter.
  const canComp = hasPermission(session, PERMISSIONS.INVOICE_COMP);

  /*
   * F1 (unit 4): starting a bill that sits at NO table.
   *
   * The entry point lives here, on the floor, because the floor is the till's
   * root — a takeaway order does not begin at a table, and routing it through
   * one is the dummy-table workaround this unit exists to remove.
   */
  /*
   * THE LIVE BILLS THAT SIT AT NO TABLE (F1), and starting one.
   *
   * The floor is built from AREAS, so a takeaway bill — which belongs to no
   * area — is its own read (`useOffPremiseBills`), with the same plate and
   * money facts a table carries, OPEN and CLOSED-but-unpaid alike (T8). The
   * PAPER floor draws them as the Takeaway section; every other look keeps
   * the rows above the floor. Starting one is the same `openInvoice` either
   * way.
   */
  const { bills: offPremiseBills } = useOffPremiseBills({ enabled: canSee });
  /* T16 — the bags out with riders: CLOSED bills and what they still owe. */
  const bagsOut = offPremiseBills
    .filter((bill) => bill.status === "CLOSED")
    .reduce(
      (sum, bill) => ({
        count: sum.count + 1,
        amount: sum.amount + (bill.amountDue ?? bill.runningTotal),
      }),
      { count: 0, amount: 0 },
    );
  const offPremiseStart = useOffPremiseStart({ enabled: canSee });
  /* Driver backend B3 · P13 — a delivery bag's driver line and the assign
     control, the same in both looks; gated on the dispatch grants inside. */
  const floorDispatch = useFloorDispatch(offPremiseBills);

  /*
   * THE TABS (PAPER, 2026-10-03): Tables, and Kitchen for a session that may
   * read the board on a store that runs it. In the URL (`?view=kitchen`), so a
   * reload keeps the tab; any other value — or a Kitchen the session cannot
   * have — is Tables.
   */
  const [params, setParams] = useSearchParams();
  const kitchenTab = useFloorKitchenTab(canSee && paper);
  /*
   * B6 · P24 (D-35): «التوصيل والتيك أواي» — everything that leaves without a
   * table, for a session that may open a bill or read the dispatch board. It
   * replaces the Tables view's takeaway strip and riders' rows (D-36).
   */
  /* 2026-10-20 — the driver's DELIVERED, told to the cashier whichever tab
     is showing, for a session that may read the dispatch board. */
  useDeliveredToasts({ enabled: floorDispatch.access.canSeeBoard });
  const delivery = useFloorDeliveryTab({
    enabled: paper,
    canClose,
    bills: offPremiseBills,
    floorDispatch,
    now,
  });
  const deliveryTab = delivery.available;
  const requested = params.get("view");
  const view: FloorView =
    paper && kitchenTab.available && requested === "kitchen"
      ? "kitchen"
      : deliveryTab && requested === "delivery"
        ? "delivery"
        : "tables";
  const showView = (next: FloorView) => {
    // Reached from a table's Kitchen tab: Tables goes back to that table, not
    // to the list (`lib/tillReturn.ts`).
    const back = tillReturnPath(params.get(TILL_RETURN_PARAM), locale);
    if (next === "tables" && back) {
      navigate(back);
      return;
    }
    const copy = new URLSearchParams(params);
    if (next === "tables") {
      copy.delete("view");
      copy.delete(TILL_RETURN_PARAM);
    } else copy.set("view", next);
    setParams(copy, { replace: true });
  };
  const canManageFloor = hasPermission(session, PERMISSIONS.FLOOR_MANAGE);
  const canSeeKitchen = hasPermission(session, PERMISSIONS.KITCHEN_VIEW);
  // Same gate the back-office nav row uses, so the exit never opens a 403 —
  // and the same one that decides whether this screen may be read at all.
  const canLeaveTill = canSee && canUseBackOffice(session);

  /*
   * B8: many open bills per table, so this counts BILLS and not busy TABLES.
   *
   * The header figure is "how much is live on this floor", and after unit 4 a
   * table with two families on it is two live bills. Counting tables would
   * under-report exactly the case the founder asked for.
   */
  const openCount =
    (data ?? []).reduce(
      (n, area) =>
        n + area.tables.reduce((m2, tbl) => m2 + tbl.openInvoices.length, 0),
      0,
    ) +
    // The off-premise bills too. The header figure is "how much is live on this
    // floor", and a takeaway waiting to be collected is live work — it was
    // excluded while the floor was built from areas alone, which under-reported
    // exactly the trade this unit exists to start recording. A CLOSED one
    // still counts: the bag is out and the money is owed (T8).
    offPremiseBills.length;
  /*
   * How many tables have a cheque OUT — closed, printed, unpaid.
   *
   * Counted whether or not a new party is already eating on the same table:
   * that cheque is still somebody's money and still this cashier's job, which
   * is the whole reason the tile tracks the two states separately.
   */
  const chequeOutCount = (data ?? []).reduce(
    (n, area) =>
      n + area.tables.filter((table) => table.closedInvoices.length > 0).length,
    0,
  );
  /*
   * The filter only APPLIES while it has a subject. When the last cheque is
   * settled the floor comes back by itself instead of leaving a cashier
   * staring at an empty grid she has to remember to un-filter — the same
   * reason the control is not offered when the count is zero.
   */
  const chequeOutFilterOn = chequeOutOnly && chequeOutCount > 0;
  /* P5-Q — the floor's table names, for the "show older" list's rows. */
  const floorTableName = (tableId: string): string =>
    (data ?? [])
      .flatMap((area) => area.tables)
      .find((table) => table.tableId === tableId)?.name ??
    t((m) => m.invoices.tableFallback, { id: tableId.slice(0, 8) });
  const pendingCount = (data ?? []).reduce(
    (n, area) =>
      n +
      area.tables.reduce(
        (m2, tbl) =>
          m2 +
          tbl.openInvoices.reduce(
            (m3, invoice) => m3 + invoice.pendingItemCount,
            0,
          ),
        0,
      ),
    0,
  );

  /*
    Server-side `@RequirePermission` is the real gate; this is so a cook or a
    manager who lands on the URL gets an explanation instead of a floor whose
    every call 403s. The way OUT is offered too, and it is a permission
    question like the landing rule (`modules/auth/landing.ts`): the kitchen for
    a cook, whose whole app is that board, and the back office for anyone else.
  */
  if (session && !canSee) {
    return (
      <Screen title={t((m) => m.pos.gridTitle)} tone="dark">
        <EmptyState
          title={t((m) => m.pos.gridDeniedTitle)}
          description={t((m) => m.pos.gridDeniedBody)}
          actionLabel={
            canSeeKitchen
              ? t((m) => m.pos.gridDeniedToKitchen)
              : canUseBackOffice(session)
                ? t((m) => m.pos.gridDeniedToManage)
                : undefined
          }
          actionHref={
            canSeeKitchen
              ? `/${locale}/kitchen`
              : canUseBackOffice(session)
                ? `/${locale}/manage`
                : undefined
          }
        />
      </Screen>
    );
  }

  return (
    <Screen
      title={paper ? t((m) => m.navPaper.floor) : t((m) => m.pos.gridTitle)}
      tone="dark"
      bleed
      phoneHead={paper}
      leading={
        /* PAPER: the redesign's bar — the way out to the back office drawn as
           its sidebar glyph, then "Store / Floor". The exit keeps its gate,
           its test id and its name. */
        paper ? (
          <PaperTillBarLeading />
        ) : (
        /* LEAVE the till, for the back office. An X, not an arrow: the floor is
           the till's root — there is nothing above it to go "back" to — so this
           closes the till rather than stepping up a tree. The invoice screen's
           control IS an arrow, because that one does step up, to this screen.

           Gated on the same permission the destination is (`POS_USE`, which is
           what `AppNav`'s manage row uses), so it is never a door onto a 403. */
        canLeaveTill ? (
          <Link
            to={`/${locale}/manage`}
            aria-label={t((m) => holdsAdministrationPermission(session?.seller.permissions) ? m.nav.manage : m.nav.cashierWorkspace)}
            title={t((m) => holdsAdministrationPermission(session?.seller.permissions) ? m.nav.manage : m.nav.cashierWorkspace)}
            className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[var(--radius-tile)] border border-[color:var(--border)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text)]"
            data-testid="pos-exit-to-manage"
            data-ui="close"
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </Link>
        ) : null
        )
      }
      action={
        /* Unit C rides the top bar now rather than a strip across the page
           (2026-09-21). It renders nothing when tracking is off or a drawer is
           open, which is every ordinary minute — so the counts beside it are
           what this slot usually holds. */
        <span className="flex items-center gap-3">
          <DrawerStrip />
          {/* F24 — how many customer requests wait on the strip below. */}
          <OnlineOrdersBadge />
          {/* PAPER: the tabs carry the counts now, so the bar does not. */}
          {!paper && data && data.length > 0 ? (
          <span className="flex items-center gap-3 text-xs text-[color:var(--text-muted)]">
            <span className="font-semibold text-[color:var(--accent)]">
              {t(
                // B118 — the fifth counted noun, and the one on the home screen.
                countedKey(openCount, {
                  one: (m) => m.pos.summaryOpenOne,
                  two: (m) => m.pos.summaryOpenTwo,
                  many: (m) => m.pos.summaryOpen,
                }),
                { count: openCount },
              )}
            </span>
            {pendingCount > 0 ? (
              <span className="font-semibold text-[color:var(--warning)]">
                {t(
  // B117/A2 — the DUAL. `plural.ts` holds the rule; this call site
  // was still spelling one form for every number.
  countedKey(pendingCount, {
    one: (m) => m.pos.pendingCountOne,
    two: (m) => m.pos.pendingCountTwo,
    few: (m) => m.pos.pendingCountFew,
    many: (m) => m.pos.pendingCount,
  }),
  { count: pendingCount },
)}
              </span>
            ) : null}
          </span>
          ) : null}
        </span>
      }
    >
      {/* PAPER draws its own skeleton under the tabs, shaped like the floor. */}
      {isLoading && !paper ? (
        <div
          aria-busy="true"
          aria-live="polite"
          aria-label={t((m) => m.common.loading)}
        >
          <LoadingSkeleton rows={6} />
        </div>
      ) : null}

      {error ? (
        <ErrorState
          // Through `errorMessage`: a dropped connection throws a TypeError
          // whose message is the browser's own English "Failed to fetch".
          message={errorMessage(error)}
          retryLabel={t((m) => m.common.retry)}
          onRetry={() =>
            void queryClient.invalidateQueries({ queryKey: queryKeys.grid })
          }
        />
      ) : null}

      {/* F24 (§5.12): the customer's own requests, above the takeaway rows
          they become once accepted. The same gutter as the rows below; the
          strip gates itself on `INVOICE_OPEN` and draws nothing it cannot
          act on. */}
      {!paper && data ? (
        <OnlineOrdersStrip paper={false} className="px-[22px] pt-[18px]" />
      ) : null}

      {/* F1: the two bills that sit at no table. Above the floor rather than
          inside it — a takeaway order does not belong to an area, and putting
          it in one would be the dummy table again wearing a different name.
          Gated on opening a bill AND the till's rule for takeaway and
          delivery (`offPremiseTabAvailable`, 2026-10-28) — not a waiter's. */}
      {!paper &&
      data &&
      canOpen &&
      offPremiseTabAvailable({ canClose, canSeeBoard: floorDispatch.access.canSeeBoard }) ? (
        /* The GUTTER, matching the scroll area below.

           These two rows are pinned above the floor rather than inside it, so
           they are outside the scroller and inherited none of its padding:
           measured, they started at x=0 and their top edge sat exactly on the
           top bar's bottom border, 0px away. `px-[22px] pt-[18px]` is that
           scroller's own padding, repeated here so the buttons line up with the
           chips and tiles beneath them instead of running edge to edge under
           the bar.

           Under a till text size (D162) the row may wrap: at large on a 400px
           phone the third door no longer fits one line. Normal keeps the one
           unwrapped row it always had (parity). */
        <div className="mb-3 flex gap-2 px-[22px] pt-[18px] [body[data-till-text-size]_&]:flex-wrap">
          {/*
            THE SHARED `Button`, not a hand-rolled one.

            These two were raw `<button>`s carrying their own border, radius,
            padding and weight. They took `data-size="touch"` but never
            `data-ui="button"` — which is the attribute the look stylesheets
            select on — so STACK, HOMEGRILL and VERDE styled every other control
            on this screen and skipped these: no 2px ink border, no hard shadow,
            no display face, while the chips and the floor tiles beside them had
            all three. That is what "doesn't look like a design button" was.

            `outline` is the variant they were imitating by hand (surface fill,
            strong border). `flex-1` stays on the className because the row
            splits them evenly; everything else comes from the primitive and
            from whichever look is mounted.
          */}
          {(["TAKEAWAY", "DELIVERY"] as const).map((channel) => (
            <Button
              key={channel}
              type="button"
              variant="outline"
              size="touch"
              data-testid={`start-${channel.toLowerCase()}`}
              onClick={() => offPremiseStart.begin(channel)}
              className="flex-1"
            >
              {channel === "DELIVERY"
                ? t((m) => m.pos.offPremiseDeliveryStart)
                : t((m) => m.pos.offPremiseTakeawayStart)}
            </Button>
          ))}
          {/* D154 — on the comp rung: the button ends in a STAFF_MEAL comp. */}
          {canComp ? (
            <Button
              type="button"
              variant="outline"
              size="touch"
              data-testid="start-staff-meal"
              onClick={offPremiseStart.beginStaffMeal}
              className="flex-1"
            >
              {t((m) => m.pos.staffMealStart)}
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* THE LIVE BILLS THAT SIT AT NO TABLE (F1).
          Rendered above the floor, beside the buttons that start them, because
          this is the ONLY screen that leads back to one: the grid is built from
          areas and a takeaway belongs to none, so without this a waiter who
          started one and walked away had lost it. */}
      {!paper && offPremiseBills.length > 0 ? (
        /* Same gutter — and the top half of it only when the buttons above are
           NOT rendered, which is the case for a member who may reach a bill but
           not start one. Otherwise this would add a second 18px gap between two
           rows that belong together. */
        /* G13 L2: the pinned block scrolls on its own past 45% of the screen,
           so many bags out never crush the tables area below it. */
        <div
          /* QA M1: the scroller must not squeeze its rows (a row with a
             driver chip needs ~67px, its button's floor is 44px). */
          className={`mb-3 flex max-h-[45svh] flex-col gap-1.5 overflow-y-auto px-[22px] [&>*]:shrink-0 ${
            data && canOpen ? "" : "pt-[18px]"
          }`}
          data-testid="off-premise-pinned"
        >
          {/* T16 — the bags out with riders, summed over the CLOSED rows. */}
          {bagsOut.count > 0 ? (
            <p
              className="text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-muted)]"
              data-testid="off-premise-out-header"
            >
              {t((m) => m.pos.outWithRiders, {
                count: bagsOut.count,
                amount: money(bagsOut.amount),
              })}
            </p>
          ) : null}
          {/* D149 — the riders' hand-in rows; the bag tiles stay below. */}
          <RiderSittings bills={offPremiseBills} dispatch={floorDispatch.sitting} />
          {offPremiseBills.map((bill) => {
            /* T8 — a CLOSED bag reads differently: "out · unpaid", what it
               still owes, how long since it left, and a tap goes straight to
               settling it. Rendered in the server's order (OPEN first, then
               CLOSED, each oldest first) so the oldest bag out is nearest. */
            const closed = bill.status === "CLOSED";
            const dispatchChip = floorDispatch.chip(bill);
            const dispatchAction = floorDispatch.action(bill, "classic");
            const row = (
              <button
                key={bill.invoiceId}
                type="button"
                data-testid={`off-premise-open-${bill.dailySequence}`}
                data-status={bill.status}
                data-size="touch"
                onClick={() =>
                  navigate(`/${locale}/pos/invoices/${bill.invoiceId}`, {
                    state: closed
                      ? ({ settle: true } satisfies InvoiceHandoff)
                      : null,
                  })
                }
                className={[
                  "pos-line-control flex w-full flex-wrap items-center gap-2 rounded-[var(--radius-control)] border bg-[color:var(--pos-tile)] px-3 py-2 text-start text-[length:calc(var(--font-size-xs)_+_0.5px)] font-extrabold text-[color:var(--text)] transition-colors hover:border-[color:var(--accent)]",
                  closed
                    ? "border-[color:var(--warning)]"
                    : "border-[color:var(--border-strong)]",
                ].join(" ")}
              >
                <span className="rounded-full bg-[color:var(--info-100)] px-2 py-[2px] text-[length:var(--font-size-2xs)] text-[color:var(--info)]">
                  {channelLabel(bill.channel, t)}
                </span>
                <span>
                  {t((m) => m.pos.invoiceNumber, {
                    sequence: bill.dailySequence,
                  })}
                </span>
                {bill.customerName ? (
                  <span className="min-w-0 truncate font-bold text-[color:var(--text-secondary)]">
                    <bdi>{bill.customerName}</bdi>
                  </span>
                ) : null}
                {bill.aggregatorName ? (
                  <span
                    className="rounded-full border border-[color:var(--border-strong)] px-2 py-[1px] text-[length:var(--font-size-2xs)] text-[color:var(--text-muted)]"
                    data-testid={`off-premise-open-${bill.dailySequence}-platform`}
                  >
                    <bdi>{bill.aggregatorName}</bdi>
                    {/* T13 — the platform's order number, what the rider says. */}
                    {bill.platformOrderRef ? <bdi dir="ltr"> · {bill.platformOrderRef}</bdi> : null}
                  </span>
                ) : null}
                {closed ? (
                  <span
                    className="rounded-full bg-[color:var(--warning)] px-2 py-[2px] text-[length:var(--font-size-2xs)] text-[color:var(--accent-contrast)]"
                    data-testid={`off-premise-open-${bill.dailySequence}-out`}
                  >
                    {t((m) => m.pos.offPremiseOutStatus)}
                    {/* T16 — who it left with. */}
                    {/* G13 L3: the driver line below says who has it; never twice. */}
                    {bill.handedTo && !dispatchChip
                      ? ` · ${t((m) => m.pos.offPremiseHandedTo, { name: bill.handedTo })}`
                      : ""}
                    {bill.closedAt
                      ? ` · ${closedSinceLabel(bill.closedAt, now, t)}`
                      : ""}
                  </span>
                ) : null}
                <span className="flex-1" />
                {/* Owed, not the running total, once the bill is closed: a
                    part-paid bag owes the rest. The same `amountDue` the
                    PAPER tile reads. */}
                <span className="tabular-nums">
                  {closed
                    ? t((m) => m.paperFloor.due, {
                        amount: money(bill.amountDue ?? bill.runningTotal),
                      })
                    : money(bill.runningTotal)}
                </span>
                {/* B3 · P13 — who has the bag, on its own line. */}
                {dispatchChip ? <span className="basis-full">{dispatchChip}</span> : null}
              </button>
            );
            /* The assign control is a SIBLING of the row's button (a button
               inside a button is invalid DOM). */
            return dispatchAction ? (
              <div key={bill.invoiceId} className="flex items-stretch gap-1.5">
                <div className="flex min-w-0 flex-1">{row}</div>
                {dispatchAction}
              </div>
            ) : (
              row
            );
          })}
        </div>
      ) : null}

      {offPremiseStart.dialog}
      {floorDispatch.sheet}

      {data && data.length === 0 && view === "tables" ? (
        <EmptyState
          title={t((m) => m.pos.gridEmptyTitle)}
          description={t((m) => m.pos.gridEmptyBody)}
          // Only offered to someone who can actually act on it — a waiter
          // sent to a screen the API will 403 is worse than no button.
          actionLabel={
            canManageFloor ? t((m) => m.pos.gridEmptyAction) : undefined
          }
          onAction={
            // Straight to the LAYOUT view (UX-round D62): this button exists to set the
            // «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
            // floor up, and the merged screen opens on the live one.
            canManageFloor
              ? () => navigate(`/${locale}/manage/floor?view=layout`)
              : undefined
          }
        />
      ) : null}

      {/* Full-bleed at the mock's 18/22 padding, and its own scroll container.
          The cap-and-centre this screen used to carry is gone deliberately: the
          redesign's floor runs the full width and lets `auto-fill` decide the
          column count, so a wide screen shows more tables rather than the same
          eight adrift in whitespace. */}
      <div
        data-ui={paper ? "till-floor" : undefined}
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-[22px] py-[18px]"
      >
        {/* The phone's head line (2026-10-28): «الصالة», who is signed in,
            then the bell and sign-out — the page's top bar is not shown. */}
        {paper && phone ? (
          <div data-ui="floor-phone-head" data-testid="floor-phone-head">
            <span data-ui="floor-phone-title">{t((m) => m.navPaper.floor)}</span>
            <span className="flex-1" />
            <TillPhoneControls />
          </div>
        ) : null}
        {/* Only with a second tab to switch to: a lone "Tables" is the
            screen's own name and costs a row (founder, 2026-10-06). */}
        {paper && (kitchenTab.available || deliveryTab) ? (
          <PaperFloorTabs<FloorView>
            value={view}
            onChange={showView}
            end={
              /* D-35 — the tab's new-order commands, on this tab only
                 (shared with the manager's floor, `FloorDeliveryTab`). */
              view === "delivery"
                ? deliveryTabCommands({ canOpen, canComp, start: offPremiseStart, t })
                : undefined
            }
            tabs={[
              {
                value: "tables",
                label: t((m) => m.paperFloor.tabTables),
                icon: FLOOR_TAB_ICONS.tables,
                count: busyCount((data ?? []).flatMap((area) => area.tables)),
                testId: "floor-tab-tables",
              },
              ...(kitchenTab.available
                ? [
                    {
                      value: "kitchen" as const,
                      label: t((m) => m.paperFloor.tabKitchen),
                      icon: FLOOR_TAB_ICONS.kitchen,
                      count: kitchenTab.count,
                      tone: kitchenTab.waiting ? ("alert" as const) : ("info" as const),
                      testId: "floor-tab-kitchen",
                    },
                  ]
                : []),
              ...(deliveryTab
                ? [
                    {
                      value: "delivery" as const,
                      label: t((m) => m.dispatch.tabDelivery),
                      icon: FLOOR_TAB_ICONS.delivery,
                      // The bags waiting for a driver; without the board, the
                      // live bills at no table.
                      count: delivery.badge.count,
                      tone: delivery.badge.tone,
                      testId: "floor-tab-delivery",
                    },
                  ]
                : []),
            ]}
          />
        ) : null}

        {paper && view === "tables" && isLoading ? (
          <PaperFloorSkeleton label={t((m) => m.common.loading)} />
        ) : null}

        {paper && view === "kitchen" ? <PaperKitchenView /> : null}

        {paper && view === "delivery" ? (
          <FloorDeliveryTab
            delivery={delivery}
            floorDispatch={floorDispatch}
            now={now}
            canOpen={canOpen}
          />
        ) : null}

        {/* One row on a phone (2026-10-28): what needs you, then the areas.
            `display: contents` from `lg` up, where they are two rows. */}
        <div data-ui="floor-filters">
        {paper && view === "tables" && data && data.length > 0 ? (
          <PaperFloorToolbar
            areas={data}
            areaFilter={areaFilter}
            onAreaFilter={setAreaFilter}
          >
            {/* The cashier's own filter, as one more chip in the row. */}
            {chequeOutCount > 0 ? (
              <ToggleChipButton
                active={chequeOutFilterOn}
                aria-pressed={chequeOutFilterOn}
                data-testid="floor-cheque-filter"
                onClick={() => setChequeOutOnly((current) => !current)}
                className="inline-flex items-center gap-1.5"
              >
                {t((m) => m.pos.tableChequeOut)}
                <span>{chequeOutCount}</span>
              </ToggleChipButton>
            ) : null}
            {/* P5-Q — cheques out more than two days have left their tiles. */}
            <OlderChequesButton tableName={floorTableName} />
          </PaperFloorToolbar>
        ) : null}

        {paper && view === "tables" && data && data.length > 0 ? (
          <PaperFloorNeeds
            counts={needCounts}
            late={needAreas.reduce((sum, area) => sum + lateIn(area.tables), 0)}
            filter={needFilter}
            onFilter={setNeedFilter}
          />
        ) : null}
        </div>

        {!paper && data && data.length > 0 ? (
          <div className="no-print flex flex-wrap items-center gap-3">
            {data.length > 1 ? (
              // The redesign's segmented area switcher: one inset track holding
              // the options, the active one lifted onto the surface colour.
              // Still a filter over a single grid, not navigation.
              <div
                role="group"
                aria-label={t((m) => m.pos.allAreas)}
                data-ui="segmented"
                className="inline-flex max-w-full gap-0.5 overflow-x-auto rounded-[var(--radius-tile)] border border-[color:var(--border-strong)] bg-[color:var(--pos-track)] p-[3px]"
              >
                {[null, ...data.map((area) => area.areaId)].map((id) => {
                  const isActive = areaFilter === id;
                  const label =
                    id === null
                      ? t((m) => m.pos.allAreas)
                      : data.find((area) => area.areaId === id)?.name ?? "";
                  return (
                    <button
                      key={id ?? "all"}
                      type="button"
                      aria-pressed={isActive}
                      data-ui="segmented-tab"
                      data-active={isActive ? "" : undefined}
                      onClick={() => setAreaFilter(id)}
                      className={[
                        "shrink-0 whitespace-nowrap rounded-[var(--radius-control)] px-3.5 py-[7px] text-xs font-bold transition-colors",
                        isActive
                          ? "bg-[color:var(--surface)] text-[color:var(--text)] shadow-[var(--shadow-sm)]"
                          : "text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
                      ].join(" ")}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            ) : null}

            {/* THE ONE STATE THAT IS THE CASHIER'S JOB, MADE REACHABLE.
                A filter over the same grid, not navigation — the same control
                vocabulary as the area chips it sits beside, and it composes
                with them. Offered only when there is a cheque out: a control
                whose only outcome is an empty floor is worse than no control,
                and the legend below already teaches the word. */}
            {chequeOutCount > 0 ? (
              <button
                type="button"
                aria-pressed={chequeOutFilterOn}
                data-size="touch"
                data-testid="floor-cheque-filter"
                onClick={() => setChequeOutOnly((current) => !current)}
                className={[
                  "inline-flex shrink-0 items-center gap-2 rounded-[var(--radius-control)] border px-3.5 py-2 text-xs font-extrabold transition-colors",
                  chequeOutFilterOn
                    ? "border-[color:var(--warning)] bg-[color:var(--warning-100)] text-[color:var(--warning)]"
                    : "border-[color:var(--border-strong)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
                ].join(" ")}
              >
                {/* The same dot the legend gives this state, so the chip and
                    the legend are visibly the same fact. */}
                <span
                  aria-hidden="true"
                  data-ui="floor-legend-dot"
                  data-state="pay"
                  className="h-[7px] w-[7px] rounded-full bg-[color:var(--warning)]"
                />
                {t((m) => m.pos.tableChequeOut)}
                <span className="tabular-nums">{chequeOutCount}</span>
              </button>
            ) : null}

            {/* P5-Q — cheques out more than two days have left their tiles. */}
            <OlderChequesButton tableName={floorTableName} />

            {/* Status legend, from the mock's floor header. Only the two states
                the grid API actually reports — there is no reservation in
                `TableGridEntry`, so the mock's third dot has no data behind it
                and is deliberately not drawn. */}
            <span data-ui="floor-legend" className="ms-auto flex items-center gap-3 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
              <span data-ui="floor-legend-item" className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  data-ui="floor-legend-dot"
                  data-state="free"
                  className="h-[7px] w-[7px] rounded-full bg-[color:var(--border-strong)]"
                />
                {t((m) => m.pos.tableFree)}
              </span>
              <span data-ui="floor-legend-item" className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  data-ui="floor-legend-dot"
                  data-state="eating"
                  className="h-[7px] w-[7px] rounded-full bg-[color:var(--info)]"
                />
                {t((m) => m.pos.tableOccupied)}
              </span>
              {/* The third state the grid can now report: the bill is printed
                  and with the customer, so the table is neither free nor
                  mid-service — it is waiting for money. */}
              <span data-ui="floor-legend-item" className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  data-ui="floor-legend-dot"
                  data-state="pay"
                  className="h-[7px] w-[7px] rounded-full bg-[color:var(--warning)]"
                />
                {t((m) => m.pos.tableChequeOut)}
              </span>
            </span>
          </div>
        ) : null}

        {/* T8 / D173 — «بانتظار أمين الصندوق»: what is waiting on the person who
            fires, above the areas, in every kitchen workflow and on both
            tills. It draws nothing while nothing waits. */}
        {view === "tables" ? <CashierWaitingLane /> : null}

        {(view === "tables" ? (data ?? []) : [])
          .filter((area) => areaFilter === null || area.areaId === areaFilter)
          // The cheque-out filter narrows the TABLES, so an area holding none
          // of them drops out rather than printing a heading over nothing.
          .map((area) =>
            chequeOutFilterOn
              ? {
                  ...area,
                  tables: area.tables.filter(
                    (table) => table.closedInvoices.length > 0,
                  ),
                }
              : area,
          )
          .filter((area) => !chequeOutFilterOn || area.tables.length > 0)
          /* Every area's tables in ONE grid on a phone while no area is
             picked (the design's floor); the chips still narrow it. */
          .reduce<TableGridArea[]>(
            (list, area) =>
              paper && phone && areaFilter === null
                ? [
                    {
                      ...area,
                      areaId: "all",
                      tables: [...(list[0]?.tables ?? []), ...area.tables],
                    },
                  ]
                : [...list, area],
            [],
          )
          .map((area) => paper ? (
            needFilter !== null &&
            !area.tables.some((table) => cards.get(table.tableId)?.group === needFilter) ? null : (
            <PhoneFlatOr flat={paper && phone && areaFilter === null} key={area.areaId} area={area} lateCount={lateIn(area.tables)}>
              {area.tables
                .filter((table) => needFilter === null || cards.get(table.tableId)?.group === needFilter)
                // Busy tables first, in their own order, then the free rows —
                // the board's layout: a free table needs nobody.
                .sort(
                  (a, b) =>
                    Number(cards.get(a.tableId)?.kind === "free") -
                    Number(cards.get(b.tableId)?.kind === "free"),
                )
                .map((table) => {
                const facts = floorTileFacts(table);
                /* No "New order" on the tile face (2026-10-03): a second party
                   at a busy table starts from the register's "New bill at
                   this table" (`OrderPanel`), and a table with several bills
                   opens their picker, which seats another under it. */
                return (
                  <PaperFloorTile
                    key={table.tableId}
                    table={table}
                    facts={facts}
                    asks={asksByTable.get(table.tableId)}
                    now={now}
                    canOpen={canOpen}
                    disabled={!facts.open && !facts.cheque && !canOpen}
                    testIdPrefix="table-"
                    onOpen={() => {
                      // The same routing as the tile below — see its notes.
                      if (facts.openBillCount > 1) {
                        navigate(`/${locale}/pos/tables/${table.tableId}`);
                      } else if (facts.open) {
                        navigate(`/${locale}/pos/invoices/${facts.open.invoiceId}`);
                      } else if (facts.cheque) {
                        navigate(`/${locale}/pos/invoices/${facts.cheque.invoiceId}`);
                      } else {
                        navigate(`/${locale}/pos/tables/${table.tableId}`);
                      }
                    }}
                  />
                );
              })}
            </PhoneFlatOr>
            )
          ) : (
          <section key={area.areaId} className="flex flex-col gap-2.5">
            {/* No `uppercase tracking-*`: an AREA NAME is store data — «الصالة»,
                «التراس» — and it renders in Arabic whatever language the UI is
                set to, so the locale-scoped trap-8 rule in `styles.css` cannot
                reach it here. Letter-spacing prises the joined script apart,
                and `uppercase` does nothing to Arabic but travelled with it. */}
            <h2 data-ui="floor-area-title" className="text-[length:var(--font-size-2xs)] font-extrabold text-[color:var(--text-muted)]">
              {area.name}
            </h2>
            {/* auto-fill at a 228px minimum, per the mock — so the floor keeps
                its tile size and simply fits fewer per row on a phone, rather
                than shrinking every tile to fit a fixed column count. */}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(min(228px,100%),1fr))] gap-3">
              {area.tables.map((table) => {
                /*
                 * B8: a table can carry SEVERAL open bills now — two families
                 * on one big table, or a table that turned while the first
                 * cheque was still out. The tile leads with the first (ordered
                 * by receipt number, so the party that arrived first) and says
                 * how many there are; it never silently picks one to navigate
                 * to, for the same reason the QR path refuses to guess.
                 */
                const openInvoices = table.openInvoices;
                const open = openInvoices[0] ?? null;
                const openBillCount = openInvoices.length;
                const manyOpen = openBillCount > 1;
                // Summed across every live bill: the tile's job is to say what
                // is on this TABLE, and with two families on it that is both.
                const openRunningTotal = openInvoices.reduce(
                  (sum, invoice) => sum + invoice.runningTotal,
                  0,
                );
                const isPending = openInvoices.some(
                  (invoice) => invoice.pendingItemCount > 0,
                );
                // Nobody has been to this table yet (D51): the diner's own QR
                // request opened it. It is a stronger statement than "there
                // are pending items", which is also true of a table a waiter
                // opened and is standing at — so it gets its own word and its
                // own tone rather than sharing the pending badge.
                const selfSeated = openInvoices.some(
                  (invoice) => invoice.selfSeated,
                );
                // D120 — a waiter has sent an order to the cashier.
                const cashierRequested = openInvoices.some((invoice) =>
                  Boolean(invoice.cashierRequestedAt),
                );
                // D153 — a live bill here names no captain (see `noCaptain`).
                const noCaptain = openInvoices.some(
                  (invoice) => invoice.hasCaptain === false,
                );
                // The cheque that is OUT: closed, printed, unpaid. A table can
                // legitimately carry BOTH — reopening a cheque and seating a
                // new party leaves the old bill closed under a new open one —
                // so the two are tracked separately rather than as one state.
                // Newest first from the server, so this is the latest cheque.
                const cheque = table.closedInvoices[0] ?? null;
                const chequeCount = table.closedInvoices.length;
                // Summed across every cheque out, for the same reason the open
                // running total is: the tile's money is the TABLE'S money. The
                // latest cheque's own figure beside a count of two read as
                // "these two are worth 319.20" when they were worth 638.40.
                const chequeTotal = table.closedInvoices.reduce(
                  (sum, row) => sum + row.total,
                  0,
                );
                const chequeOnly = !open && cheque !== null;
                // Round 5 — the pre-bill check, by the one rule the PAPER tile
                // reads too (`floorTileFacts`), so the two floors cannot
                // disagree about whether the paper on a table is still right.
                const tileFacts = floorTileFacts(table);
                const checkState = tileFacts.check;
                const tile = (
                  <button
                    key={chequeOnly && canOpen ? undefined : table.tableId}
                    type="button"
                    disabled={!open && !cheque && !canOpen}
                    data-testid={`table-${table.code}`}
                    data-ui="floor-tile"
                    data-size="touch"
                    data-state={open ? "eating" : chequeOnly ? "pay" : "free"}
                    onClick={() => {
                      // A tap never opens an invoice — it only navigates.
                      // The table goes occupied on "Start order", one screen
                      // in, so a mis-tap on the floor costs nothing.
                      //
                      // OPEN wins when both exist: the live ticket is what the
                      // next tap on this table is almost always about, and the
                      // cheque is still reachable from history. The badge on
                      // the tile is what says the old bill is still out.
                      if (manyOpen) {
                        // SEVERAL live bills: the tile cannot know which family
                        // the next tap is about, and picking one would open a
                        // stranger's ticket. The table screen lists them.
                        navigate(`/${locale}/pos/tables/${table.tableId}`);
                      } else if (open) {
                        navigate(`/${locale}/pos/invoices/${open.invoiceId}`);
                      } else if (cheque) {
                        // Where Settle lives, and the only screen it lives on.
                        navigate(`/${locale}/pos/invoices/${cheque.invoiceId}`);
                      } else {
                        navigate(`/${locale}/pos/tables/${table.tableId}`);
                      }
                    }}
                    // An open table reads as open from three things, not just
                    // the accent border: the invoice number, the running total,
                    // and the filled surface. Colour alone is never the signal.
                    className={[
                      "pos-tile pos-tile-floor flex min-h-[116px] w-full flex-col items-start gap-[9px] rounded-[var(--radius-card)] border p-[17px] pb-[15px] text-start",
                      // The artboard gives every tile the SAME surface and
                      // separates the states by border and badge alone — no
                      // dashed outline, no muted fill. Occupied steps its
                      // border up to --ink4 (this scope's --text-disabled),
                      // which is also what any tile takes on hover.
                      "bg-[color:var(--pos-tile)] disabled:opacity-60",
                      open
                        ? "border-[color:var(--text-disabled)]"
                        : chequeOnly
                          ? // Warning tone, and only on the border+badge+words:
                            // a cheque out is an unfinished piece of work, not
                            // a failure, and never carried by colour alone.
                            "border-[color:var(--warning)]"
                          : "border-[color:var(--pos-tile-br)]",
                    ].join(" ")}
                  >
                    <span className="flex w-full items-center gap-2">
                      <span className="text-lg font-extrabold tracking-[-0.01em]">
                        {table.name}
                      </span>
                      <span className="flex-1" />
                      {/* Badge tones are the artboard's: occupied reads INFO,
                          not success — blue is "someone is sitting here", and
                          green would imply a resolved state on a table that is
                          mid-service. Free reads as a quiet chip. */}
                      <span
                        className={[
                          // Same reason as the area title above: a table's
                          // state chip is rendered from store data and reads
                          // in Arabic in both locales.
                          "rounded-full px-2.5 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold",
                          open
                            ? isPending
                              ? "bg-[color:var(--warning-100)] text-[color:var(--warning)]"
                              : "bg-[color:var(--info-100)] text-[color:var(--info)]"
                            : chequeOnly
                              ? "bg-[color:var(--warning-100)] text-[color:var(--warning)]"
                              : "bg-[color:var(--pos-chip)] text-[color:var(--text-muted)]",
                        ].join(" ")}
                      >
                        {open
                          ? manyOpen
                          /* QA3 — the chip is gated on `> 1`, so the DUAL is
                             the smallest value it can ever show and it read
                             «2 حسابات». Through the helper like every other
                             counted noun; `one` is supplied although the gate
                             makes it unreachable, because the next gate that
                             changes would otherwise reintroduce the bug. */
                            ? t(
                                countedKey(openBillCount, {
                                  one: (m) => m.pos.tableBillCountOne,
                                  two: (m) => m.pos.tableBillCountTwo,
                                  few: (m) => m.pos.tableBillCountFew,
                                  many: (m) => m.pos.tableBillCount,
                                }),
                                { count: openBillCount },
                              )
                            : t((m) => m.pos.tableOccupied)
                          : chequeOnly
                            ? chequeCount > 1
                              ? t(
                                  // Same gate, same rule — see the bill chip.
                                  countedKey(chequeCount, {
                                    one: (m) => m.pos.tableChequeCountOne,
                                    two: (m) => m.pos.tableChequeCountTwo,
                                    few: (m) => m.pos.tableChequeCountFew,
                                    many: (m) => m.pos.tableChequeCount,
                                  }),
                                  { count: chequeCount },
                                )
                              : t((m) => m.pos.tableChequeOut)
                            : t((m) => m.pos.tableFree)}
                      </span>
                    </span>

                    {/* One meta line, the artboard's composition:
                        `#no · N items · total`, in full-strength ink when the
                        table is occupied and muted when it is free. */}
                    <span
                      className={[
                        "text-[length:calc(var(--font-size-xs)_+_0.5px)] font-bold tabular-nums",
                        open || chequeOnly
                          ? "text-[color:var(--text)]"
                          : "text-[color:var(--text-disabled)]",
                      ].join(" ")}
                    >
                      {open
                        ? // The artboard's meta is `#no · N items · total`.
                          // `TableGridEntry.openInvoice` carries no item count,
                          // so the middle term is dropped rather than faked —
                          // adding it would mean a second request per tile.
                          [
                            /*
                             * With several bills the receipt number of one of
                             * them would be a half-truth on a tile describing
                             * the table — so it is DROPPED rather than replaced
                             * by the count, which the badge above already says.
                             * Printing the count in both places made the tile
                             * read "4 bills" directly above "4 bills · 638.40".
                             */
                            ...(manyOpen
                              ? []
                              : [
                                  t((m) => m.pos.invoiceNumber, {
                                    sequence: open.dailySequence,
                                  }),
                                ]),
                            money(openRunningTotal),
                          ].join(" · ")
                        : chequeOnly && cheque
                          ? // What is owed and what to do about it. `total` on
                            // a closed bill is the finalised figure, so this is
                            // the number printed on the paper in the
                            // customer's hand — not a running estimate.
                            [
                              money(chequeTotal),
                              t((m) => m.pos.tableChequeOutHint),
                            ].join(" · ")
                          : canOpen
                            ? t((m) => m.pos.tapToStart)
                            : t((m) => m.pos.tableFree)}
                    </span>

                    {/* Audit round 2, Phase 3 — what the kitchen has for
                        this table and whether a plate is late, the same line
                        the PAPER tile draws under its status word. Muted ink;
                        «متأخر» is a word in the warning tone, never colour
                        alone. */}
                    {open ? (
                      <FloorTileKitchenLine
                        plates={tileFacts.plates}
                        late={tileFacts.late}
                        testId={`table-${table.code}`}
                        className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]"
                        lateClassName="font-extrabold text-[color:var(--warning)]"
                      />
                    ) : null}

                    {/* WRAPPING, since D51: a table can now carry three
                        things at once — how long it has been open, a cheque
                        still out, "self-seated" and the pending count. Without
                        `flex-wrap` the fourth one squeezed the elapsed label
                        onto two lines mid-word ("من ١" / "د"). Each piece is
                        `whitespace-nowrap`, so the row breaks BETWEEN badges
                        and never inside one. */}
                    <span className="flex w-full flex-1 flex-wrap items-end gap-1.5 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
                      {open ? (
                        <span className="whitespace-nowrap">
                          {formatOpenFor(open.openedAt, t)}
                        </span>
                      ) : null}
                      {/* BOTH: an old cheque is still out under a new party.
                          The tap goes to the live ticket, so the badge is what
                          keeps the unpaid bill from disappearing off the floor
                          — the exact hole this state was added to close. */}
                      {open && cheque ? (
                        <span className="whitespace-nowrap rounded-full bg-[color:var(--warning-100)] px-2 py-0.5 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--warning)]">
                          {t((m) => m.pos.tableChequeOut)}
                        </span>
                      ) : null}
                      {/* ROUND 5 — A CHECK IS IN THE GUESTS' HANDS. Warning
                          tone while it still is the bill; the danger fill once
                          the bill moved after it, because that paper is now
                          wrong and the remedy is a reprint. Words, not colour
                          alone. */}
                      {checkState ? (
                        <span
                          data-testid={`table-${table.code}-check-${checkState.state}`}
                          className={[
                            "whitespace-nowrap rounded-full px-2 py-0.5 text-[length:var(--font-size-2xs)] font-bold",
                            checkState.state === "stale"
                              ? "bg-[color:var(--danger)] text-[color:var(--accent-contrast)]"
                              : "bg-[color:var(--warning-100)] text-[color:var(--warning)]",
                          ].join(" ")}
                        >
                          {checkState.state === "stale"
                            ? t((m) => m.preBillCheck.tileStale)
                            : t((m) => m.preBillCheck.tilePrinted)}
                        </span>
                      ) : null}
                      {open && selfSeated ? (
                        /* INFO tone, and a word — "a party seated itself" is
                           news, not a problem, and the accompanying pending
                           badge already carries the urgency. Colour is never
                           the only carrier: the sentence says it too. */
                        <span
                          data-testid={`table-${table.code}-self-seated`}
                          className="whitespace-nowrap rounded-full bg-[color:var(--info)] px-2 py-0.5 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--accent-contrast)]"
                        >
                          {t((m) => m.pos.selfSeatedBadge)}
                        </span>
                      ) : null}
                      {open && cashierRequested ? (
                        <span
                          data-testid={`table-${table.code}-cashier-requested`}
                          className="whitespace-nowrap rounded-full bg-[color:var(--warning-100)] px-2 py-0.5 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--warning)]"
                        >
                          {t((m) => m.pos.cashierWaitingBadge)}
                        </span>
                      ) : null}
                      {/* D153 — quiet, toneless: a note, not an alarm. */}
                      {open && noCaptain ? (
                        <span
                          data-testid={`table-${table.code}-no-captain`}
                          className="whitespace-nowrap rounded-full border border-[color:var(--border)] px-2 py-0.5 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]"
                        >
                          {t((m) => m.captain.none)}
                        </span>
                      ) : null}
                      {/* B117/Q8 — A GUEST SAYS THEY PAID. Before the pending
                          chip, because this one is about MONEY already claimed
                          to have moved and the other is about food not yet
                          accepted — and a cashier reading a row of tiles should
                          meet the money first.
                          
                          `danger`, where the pending chip is `warning`: an
                          unanswered claim ends with a drawer that is OVER, which
                          is the cashier being asked to explain taking money
                          twice. */}
                      {open && open.hasPendingPaymentClaim ? (
                        <span
                          data-testid={`table-${table.code}-payment-claim`}
                          className="whitespace-nowrap rounded-full bg-[color:var(--danger)] px-2 py-0.5 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--accent-contrast)]"
                        >
                          {t((m) => m.pos.paymentClaimBadge)}
                        </span>
                      ) : null}
                      {open && isPending ? (
                        <span className="whitespace-nowrap rounded-full bg-[color:var(--warning)] px-2 py-0.5 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--accent-contrast)]">
                          {t(
                            // B117/A2+D11 — the dual, on the tile's own badge
                            // as well as the page header's.
                            countedKey(open.pendingItemCount, {
                              one: (m) => m.pos.pendingCountOne,
                              two: (m) => m.pos.pendingCountTwo,
                              few: (m) => m.pos.pendingCountFew,
                              many: (m) => m.pos.pendingCount,
                            }),
                            { count: open.pendingItemCount },
                          )}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
                if (!(canOpen && (open || cheque))) return tile;
                // Another party can sit at a table while an earlier bill is open
                // or unpaid. Keep that deliberate action separate from opening
                // the current ticket.
                // The MAIN tap stays on the current bill (walking past it
                // must be a choice, not an accident); this sibling is the
                // deliberate second choice. A sibling, not a child: a button
                // inside a button is invalid DOM, so the tile is wrapped and
                // the control positioned into its footer corner — the same
                // shape the menu tile's 86 control uses.
                return (
                  <span key={table.tableId} className="relative flex">
                    {tile}
                    <button
                      type="button"
                      data-testid={`table-${table.code}-new-order`}
                      onClick={() =>
                        navigate(`/${locale}/pos/tables/${table.tableId}?new=1`)
                      }
                      className="pos-line-control absolute bottom-[13px] end-[15px] rounded-[var(--radius-control)] border border-[color:var(--pos-tile-br)] bg-[color:var(--pos-tile)] px-2.5 py-[5px] text-[length:var(--font-size-2xs)] font-extrabold text-[color:var(--text-secondary)] transition-colors hover:text-[color:var(--text)]"
                    >
                      {t((m) => m.pos.tableNewOrder)}
                    </button>
                  </span>
                );
              })}
            </div>
            </section>
          ))}

        {/* The colour key is the desk's; the phone design draws none. */}
        {paper && !phone && view === "tables" && data && data.length > 0 ? <PaperFloorLegend /> : null}

        {/* B6 · P24 (D-35/D-36): the customers' requests, the takeaway and
            delivery tiles and the riders' hand-in rows moved to the Delivery
            & takeaway tab — the Tables view is the tables. */}
      </div>
    </Screen>
  );
}

/**
 * An area's tiles under its heading — or, on the phone's flat floor
 * (2026-10-28), the one grid with no heading at all.
 */
function PhoneFlatOr({
  flat,
  area,
  lateCount,
  children,
}: {
  flat: boolean;
  area: TableGridArea;
  lateCount: number;
  children: ReactNode;
}) {
  return flat ? (
    <div data-ui="floor-grid" data-testid="floor-phone-grid" className="grid">
      {children}
    </div>
  ) : (
    <PaperAreaSection area={area} lateCount={lateCount}>
      {children}
    </PaperAreaSection>
  );
}
